import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { graphql } from '@apollo/client/react/hoc';
import InfiniteScroll from 'react-infinite-scroller';
import isEqual from 'lodash/isEqual';
import PulseLoader from 'react-spinners/PulseLoader';

// app modules
import { CollectionsMatrix } from 'components/Matrix';
import SortingOptions from 'components/SortingOptions';
import CollectionsEditorial from 'components/CollectionsEditorial';

// app modules: constants
import { COLOURS } from 'constants/index';

// app modules: props
import SortingOptionsProps from 'components/SortingOptions/SortingOptionsProps';

// app modules: queries
import COLLECTIONS_QUERY from './CollectionsGraphQL';

/* eslint-disable react/jsx-no-bind */
export class CollectionsViewComponent extends React.Component {
  static propTypes = {
    data: PropTypes.shape({
      Collections: PropTypes.array,
      loading: PropTypes.boolean,
      error: PropTypes.object,
      fetchMore: PropTypes.func,
    }),
    sortingOptions: SortingOptionsProps.isRequired,
    tags: PropTypes.arrayOf(PropTypes.string),
    // eslint-disable-next-line react/no-unused-prop-types
    excludedTags: PropTypes.arrayOf(PropTypes.string),
    // eslint-disable-next-line react/no-unused-prop-types
    excludeOwn: PropTypes.bool,
  };

  static defaultProps = {
    data: {
      Collections: [],
      loading: false,
      error: {},
    },
    tags: [],
    excludedTags: [],
    excludeOwn: false,
  };

  constructor(props) {
    super(props);

    this.state = {
      hasMore: true,
    };
  }

  componentDidUpdate(prevProps) {
    const { fetchMore } = this.props.data;
    const { sortingOptions } = this.props;
    const self = this;
    if (!isEqual(prevProps.sortingOptions, sortingOptions)) {
      fetchMore({
        variables: {
          offset: 0,
          orderBy: sortingOptions.orderBy,
          orderByDirection: sortingOptions.orderByDirection,
        },

        updateQuery: (previousResult, { fetchMoreResult }) => {
          if (!fetchMoreResult.Collections) {
            self.noMoreResults();
            return previousResult;
          }
          return fetchMoreResult;
        },
      });
    }
  }

  noMoreResults() {
    this.setState({
      hasMore: false,
    });
  }

  /**
   * @param  {Number} page
   */
  loadNextPage() {
    const self = this;
    const { fetchMore } = this.props.data;
    const { orderBy, orderByDirection } = this.props.sortingOptions;

    fetchMore({
      variables: {
        offset: this.props.data.Collections.length,
        orderBy,
        orderByDirection,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult.Collections) {
          self.noMoreResults();
          return previousResult;
        }

        return Object.assign({}, previousResult, {
          Collections: [...previousResult.Collections, ...fetchMoreResult.Collections],
        });
      },
    });
  }

  render() {
    const { data, sortingOptions, tags } = this.props;
    const { hasMore } = this.state;

    if (data && data.loading) {
      return (
        <Fragment>
          <CollectionsEditorial tag={tags[0]} />

          <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <PulseLoader loading color={COLOURS.STABLE} sizeUnit="px" size={10} />
          </div>
        </Fragment>
      );
    }

    if (data && data.error) {
      console.error(data.error);
      return <div>Failed to load collections - please try again later.</div>;
    }

    return (
      <Fragment>
        <CollectionsEditorial tag={tags[0]} />
        <div className="in-column">
          <SortingOptions
            selected={sortingOptions}
            options={[
              {
                key: 'available_from',
                label: 'Recently added',
                direction: 'desc',
                path: `/collections/${tags[0]}/recent`,
              },
              {
                key: 'name',
                label: 'Title A-Z',
                direction: 'asc',
                path: `/collections/${tags[0]}/az`,
              },
            ]}
          />
          <InfiniteScroll
            hasMore={hasMore}
            initialLoad={false}
            loadMore={this.loadNextPage.bind(this)}
            pageStart={0}
          >
            <CollectionsMatrix
              itemProps={{ mobileViewAsList: true }}
              items={data.Collections}
              showAll
              withAdverts
            />
          </InfiniteScroll>
        </div>
      </Fragment>
    );
  }
}

const withCollectionsQuery = graphql(COLLECTIONS_QUERY, {
  options({ sortingOptions, tags, excludedTags, excludeOwn }) {
    const { orderBy, orderByDirection } = sortingOptions;
    return {
      variables: {
        offset: 0,
        limit: 20,
        orderBy,
        orderByDirection,
        tags,
        excludedTags,
        excludeOwn,
      },
    };
  },
});

const mapStateToProps = (state, ownProps) => ({
  sortingOptions: state.entitiesList[`collections-${ownProps.tags[0]}`] || {
    orderBy: null,
    orderByDirection: null,
  },
  router: state.router,
});

export default connect(
  mapStateToProps,
  null,
)(withCollectionsQuery(CollectionsViewComponent));
